import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { MapPanel } from "@/components/dashboard/map/map-panel";
import { MapSurface } from "@/components/dashboard/map/map-surface";
import { PersonAvatar } from "@/components/employees/person-avatar";
import { PageHeader } from "@/components/dashboard/page-header";
import { RecordNotFound } from "@/components/dashboard/record-not-found";
import { SkeletonPanel } from "@/components/dashboard/skeletons";
import { StatusPill, type StatusTone } from "@/components/dashboard/status-pill";
import { IncidentStatusPanel } from "@/components/incidents/status-panel";
import { incidents as copy, nav } from "@/content";
import { can } from "@/constants/permissions";
import { apiUrl } from "@/config";
import {
  getIncidentReport,
  type IncidentStatus,
} from "@/api/incidents";
import { useQuery } from "@tanstack/react-query";
import { useFmt } from "@/hooks/shared/useFmt";
import { useSession } from "@/context/UserContext";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";

const STATUS_TONE: Record<IncidentStatus, StatusTone> = {
  new: "attention",
  under_review: "muted",
  resolved: "positive",
  rejected: "negative",
};

const STATUS_LABEL: Record<IncidentStatus, string> = {
  new: copy.statusNew,
  under_review: copy.statusUnderReview,
  resolved: copy.statusResolved,
  rejected: copy.statusRejected,
};

export const Route = createFileRoute("/_main/dashboard/incidents/$id")({
  component: () => (
    <RequirePermission action="incidents.read">
      <IncidentDetailPage />
    </RequirePermission>
  ),
});

function IncidentDetailPage() {
  useDocumentTitle(copy.requestTitle);
  const { user, settings } = useSession();
  const { id } = Route.useParams() as { id: string };
  const requestId = Number(id);
  const validId = Number.isInteger(requestId) && requestId >= 1;
  const fmt = useFmt();

  const requestQuery = useQuery({
    queryKey: ["incidents", "get", requestId],
    queryFn: () => getIncidentReport(requestId),
    enabled: validId,
  });
  const request = requestQuery.data;

  if (!validId || (requestQuery.isFetched && !request)) return <RecordNotFound />;
  if (!request) {
    return (
      <div className="mx-auto w-full max-w-shell px-6 py-8">
        <SkeletonPanel height="20rem" />
      </div>
    );
  }

  const hasLocation = request.lat !== null && request.lng !== null;
  const centre = hasLocation
    ? { lat: request.lat!, lng: request.lng! }
    : { lat: settings.defaultMapLat, lng: settings.defaultMapLng };

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrow={nav.groups.operations}
        title={request.guardName}
        lead={`${copy.requestTitle} · ${request.reportType.nameAr}`}
        actions={
          <Link
            to="/dashboard/incidents"
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-n4 bg-n0 px-4 text-sm font-semibold text-n8 transition-colors duration-200 ease-brand hover:bg-n2"
          >
            <ArrowRight aria-hidden className="size-4" strokeWidth={1.6} />
            {copy.backToQueue}
          </Link>
        }
      />

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-6">
          <div className="mb-4 flex items-center gap-2.5">
            <PersonAvatar name={request.guardName} photoUrl={request.photoUrl} size="md" />
            <Link
              to={`/dashboard/employees/${request.guardId}`}
              className="text-sm font-semibold text-orange-700 transition-opacity duration-200 ease-brand hover:opacity-80"
            >
              {request.guardName}
            </Link>
          </div>
          <dl className="grid gap-4 rounded-lg border border-n3 bg-n0 p-5 sm:grid-cols-2">
            <Cell label={copy.columnStatus}>
              <StatusPill tone={STATUS_TONE[request.status]}>
                {STATUS_LABEL[request.status]}
              </StatusPill>
            </Cell>
            <Cell label={copy.columnSubmitted}>
              <span className="tabular" dir="ltr">
                {fmt.dateTime(request.createdAt)}
              </span>
            </Cell>
            <Cell label={copy.columnType}>
              {request.reportType.nameAr}
              {request.otherLabel ? (
                <span className="mt-1 block text-2xs text-n6">
                  {copy.otherLabel}: {request.otherLabel}
                </span>
              ) : null}
            </Cell>
            <Cell label={copy.columnArea}>{request.zoneName ?? "—"}</Cell>
            <Cell label={copy.supervisor}>{request.supervisorName ?? "—"}</Cell>
            <Cell label={copy.reviewedBy}>{request.reviewedByName ?? "—"}</Cell>
            <Cell label={copy.reviewedAt}>
              {request.reviewedAt ? (
                <span className="tabular" dir="ltr">
                  {fmt.dateTime(request.reviewedAt)}
                </span>
              ) : (
                "—"
              )}
            </Cell>
            <Cell label={copy.description} className="sm:col-span-2">
              <p className="whitespace-pre-wrap text-sm text-n8">{request.description}</p>
            </Cell>
            {request.adminNotes ? (
              <Cell label={copy.adminNotes} className="sm:col-span-2">
                <p className="whitespace-pre-wrap text-sm text-n8">{request.adminNotes}</p>
              </Cell>
            ) : null}
            <Cell label={copy.attachments} className="sm:col-span-2">
              {request.attachments.length === 0 ? (
                <span className="text-n6">{copy.noAttachments}</span>
              ) : (
                <ul className="space-y-2">
                  {request.attachments.map((file, index) => (
                    <li key={file.id}>
                      <a
                        href={apiUrl(`/api/files/${encodeURIComponent(file.fileKey)}`)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm font-semibold text-orange underline-offset-2 hover:underline"
                      >
                        {copy.attachments} {index + 1}
                        <span className="ms-2 text-2xs font-normal text-n6" dir="ltr">
                          ({file.contentType})
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </Cell>
          </dl>

          <div>
            <h2 className="mb-3 text-sm font-semibold text-n9">{copy.location}</h2>
            {hasLocation ? (
              <MapPanel title={copy.location}>
                <MapSurface
                  zones={[]}
                  centre={centre}
                  marker={{ lat: request.lat!, lng: request.lng! }}
                  numerals={user.numeralSystem}
                />
              </MapPanel>
            ) : (
              <p className="text-sm text-n6">{copy.noLocation}</p>
            )}
          </div>
        </div>

        <IncidentStatusPanel
          id={request.id}
          status={request.status}
          adminNotes={request.adminNotes}
          canUpdate={can(user.role, "incidents.update")}
        />
      </div>
    </div>
  );
}

function Cell({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-2xs font-semibold text-n6">{label}</dt>
      <dd className="mt-1 text-sm text-n9">{children}</dd>
    </div>
  );
}
